<script setup lang="ts">
    import { ref, watch } from 'vue';
    import draggable from 'vuedraggable';
    import DashboardNavigationItem from './DashboardNavigationItem.vue';
    import { DashboardListItem } from '@/js/services/api/dashboard/types';

    const props = withDefaults(
        defineProps<{
            dashboards: DashboardListItem[];
            groupName: string;
            activeId?: string;
            emptyState?: string;
            sortable?: boolean;
        }>(),
        { sortable: false }
    );

    const emit = defineEmits<{
        (e: 'select', id: string): void;
        (e: 'favoriteToggle', id: string): void;
        (e: 'reorder', orderedIds: string[]): void;
    }>();

    const localItems = ref<DashboardListItem[]>([...props.dashboards]);

    watch(
        () => props.dashboards,
        (next) => {
            localItems.value = [...next];
        }
    );

    const onDragEnd = () => {
        emit(
            'reorder',
            localItems.value.map((item) => item.id)
        );
    };
</script>

<template>
    <draggable
        v-if="sortable && localItems.length > 0"
        v-model="localItems"
        :group="{ name: groupName, pull: false, put: false }"
        item-key="id"
        handle=".drag-handle"
        class="flex flex-col"
        ghost-class="opacity-50"
        @end="onDragEnd"
    >
        <template #item="{ element }">
            <DashboardNavigationItem
                :dashboard="element"
                :active="element.id === activeId"
                sortable
                @select="emit('select', $event)"
                @favorite-toggle="emit('favoriteToggle', $event)"
            />
        </template>
    </draggable>
    <div v-else-if="localItems.length > 0" class="flex flex-col">
        <DashboardNavigationItem
            v-for="item in localItems"
            :key="item.id"
            :dashboard="item"
            :active="item.id === activeId"
            @select="emit('select', $event)"
            @favorite-toggle="emit('favoriteToggle', $event)"
        />
    </div>
    <p v-else-if="emptyState" class="text-gray-medium py-1.5">
        {{ emptyState }}
    </p>
</template>
